import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalShell from "../../ModalShell";
import type { Editor, Engine } from "../../../services/packages";
import type { CanvasTemplateConfig } from "../../../utils/canvasTemplateConfig";
import { canvasTemplateConfigFromDescriptor } from "../../../utils/canvasTemplateConfig";
import { tryGetNodeDescriptor } from "../../../registry/nodeRegistry";
import { NodeTemplateId } from "../../../registry/types";
import { TemplatePortEditor } from "./TemplatePortEditor";
import styles from "./NodeTemplateConfigModal.module.css";

export function NodeTemplateConfigModal({
  show,
  nodeId,
  nodeType,
  storedConfig,
  storedLabel,
  templateCode,
  onClose,
  onSave,
}: {
  show: boolean;
  nodeId: string;
  nodeType: NodeTemplateId;
  storedConfig: Partial<CanvasTemplateConfig> | null;
  storedLabel?: string;
  /** Runtime editor body used to seed stored template fields (not edited in this modal). */
  templateCode: string;
  onClose: () => void;
  onSave: (config: CanvasTemplateConfig) => void;
}) {
  const desc = useMemo(() => tryGetNodeDescriptor(nodeType), [nodeType]);
  const [config, setConfig] = useState<CanvasTemplateConfig | null>(null);

  useEffect(() => {
    if (!show || !desc) return;
    const nodeData = {
      packageTemplateConfig: storedConfig ?? undefined,
      packageTemplateLabel: storedLabel,
    };
    setConfig(
      canvasTemplateConfigFromDescriptor(
        desc,
        { id: nodeId, data: nodeData } as { id: string; data: object },
        templateCode,
      ),
    );
  }, [show, desc, nodeId, storedConfig, storedLabel, templateCode]);

  const patch = useCallback((p: Partial<CanvasTemplateConfig>) => {
    setConfig((prev) => (prev ? { ...prev, ...p } : prev));
  }, []);

  const onEditorChange = useCallback((editor: Editor) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const next = { ...prev, editor };
      if (editor === "code") {
        next.hasCode = true;
        next.hasGrammar = false;
        next.engine = "python";
      } else if (editor === "grammar") {
        next.hasCode = false;
        next.hasGrammar = true;
        next.hasWidgets = false;
        next.engine = "javascript";
      } else if (editor === "widgets") {
        next.hasCode = true;
        next.hasWidgets = true;
        next.hasGrammar = false;
      } else {
        next.hasCode = false;
        next.hasWidgets = false;
        next.hasGrammar = false;
      }
      return next;
    });
  }, []);

  if (!show || !desc || !config) return null;

  const isReadOnly = desc.package?.readOnly === true;

  return (
    <ModalShell preservePackagePaletteOpen onClose={onClose} size="large" layer="overlay" titleId="node-settings-title">
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <h2 id="node-settings-title" className={styles.title}>Node settings</h2>
          {isReadOnly && <span className={styles.readOnlyBadge}>Read-only</span>}
        </div>
        <p className={styles.subtitle}>
          {isReadOnly
            ? "This node belongs to a read-only package. Save your changes as a new package."
            : "Edit kind metadata, ports, and editor tabs. Saving opens the Save As flow to commit the changes into a package."}
        </p>

        <p className={styles.sectionTitle}>Identity</p>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="kind-config-label">
            Node title
          </label>
          <input
            id="kind-config-label"
            className={styles.input}
            value={config.label}
            onChange={(e) => patch({ label: e.target.value })}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="kind-config-description">
            Description
          </label>
          <textarea
            id="kind-config-description"
            className={styles.textarea}
            style={{ minHeight: 72 }}
            value={config.description}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </div>

        <p className={styles.sectionTitle}>Editor</p>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="kind-config-editor">
              Editor mode
            </label>
            <select
              id="kind-config-editor"
              className={styles.select}
              value={config.editor}
              onChange={(e) => onEditorChange(e.target.value as Editor)}
            >
              <option value="code">code</option>
              <option value="widgets">widgets</option>
              <option value="grammar">grammar</option>
              <option value="none">none</option>
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="kind-config-engine">
              Engine
            </label>
            <select
              id="kind-config-engine"
              className={styles.select}
              value={config.engine}
              onChange={(e) => patch({ engine: e.target.value as Engine })}
            >
              <option value="python">python</option>
              <option value="javascript">javascript</option>
            </select>
          </div>
        </div>

        {/* The visible text is the accessible name here (the input is wrapped
            in its label), so these read as the internal field names to a
            screen reader too until they say something (#219). */}
        <p className={styles.sectionTitle}>Capabilities</p>
        <div className={styles.checkGrid}>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={config.hasCode}
              onChange={(e) => patch({ hasCode: e.target.checked })}
            />
            Code
          </label>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={config.hasWidgets}
              onChange={(e) => patch({ hasWidgets: e.target.checked })}
            />
            Widgets
          </label>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={config.hasGrammar}
              onChange={(e) => patch({ hasGrammar: e.target.checked })}
            />
            Grammar
          </label>
        </div>

        <p className={styles.sectionTitle}>Header</p>
        <div className={styles.checkGrid}>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={config.hasProvenance}
              onChange={(e) => patch({ hasProvenance: e.target.checked })}
            />
            Provenance button
          </label>
        </div>

        <p className={styles.sectionTitle}>Ports</p>
        <TemplatePortEditor
          title="Input ports"
          ports={config.inputPorts}
          onChange={(inputPorts) => patch({ inputPorts })}
        />
        <TemplatePortEditor
          title="Output ports"
          ports={config.outputPorts}
          onChange={(outputPorts) => patch({ outputPorts })}
        />

        <div className={styles.footer}>
          <button type="button" className={styles.ghostBtn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.primaryBtn} onClick={() => onSave(config)}>
            Save as package node…
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

